import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { GetApp, Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import EmergencyQuickActions from '../../components/workforceMonitoring/EmergencyQuickActions';

const KpiCard: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5">{value}</Typography>
    </CardContent>
  </Card>
);

const OverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const workDate = moment().format('YYYY-MM-DD');

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['monitoringOverviewEnhanced', workDate],
    queryFn: () => apiClient.monitoring.getOverview({ date: workDate }),
    refetchInterval: 30000,
  });

  const { data: healthData } = useQuery({
    queryKey: ['trackingHealth'],
    queryFn: () => apiClient.tracking.getHealth(),
    refetchInterval: 60000,
  });

  const { data: installerData } = useQuery({
    queryKey: ['installerLatest'],
    queryFn: () => apiClient.installer.getLatest(),
  });

  const overview = data?.data?.data;
  const kpis = overview?.kpis;

  const installerInfo = installerData?.data?.data;
  const installerVersion = installerInfo?.available ? installerInfo.version : null;

  const handleDownloadInstaller = () => {
    if (!installerInfo?.available) {
      alert(installerInfo?.message || 'Installer not available.');
      return;
    }
    window.location.href = installerInfo.downloadUrl || `/api/installer/download/${installerInfo.version}`;
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1">Today — {moment(workDate).format('MMM D, YYYY')}</Typography>
        <Box display="flex" gap={1}>
          <Button size="small" startIcon={<GetApp />} variant="outlined" onClick={handleDownloadInstaller}>
            {installerVersion ? `Agent Installer v${installerVersion}` : 'Agent Installer'}
          </Button>
          <Button size="small" startIcon={<RefreshIcon />} variant="outlined" onClick={() => refetch()} disabled={isFetching}>
            Refresh
          </Button>
        </Box>
      </Box>

      {healthData?.data?.data?.status !== 'healthy' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Tracking system health: {healthData?.data?.data?.status || 'unknown'}
        </Alert>
      )}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}><KpiCard label="Registered" value={kpis?.registered_computers ?? 0} /></Grid>
        <Grid item xs={6} sm={4} md={2}><KpiCard label="Online" value={kpis?.online ?? 0} /></Grid>
        <Grid item xs={6} sm={4} md={2}><KpiCard label="Offline" value={kpis?.offline ?? 0} /></Grid>
        <Grid item xs={6} sm={4} md={2}><KpiCard label="Working" value={kpis?.employees_working ?? 0} /></Grid>
        <Grid item xs={6} sm={4} md={2}><KpiCard label="Locked" value={kpis?.locked ?? 0} /></Grid>
        <Grid item xs={6} sm={4} md={2}><KpiCard label="Lunch" value={kpis?.lunch ?? 0} /></Grid>
        <Grid item xs={6} sm={4} md={2}><KpiCard label="Break" value={kpis?.break ?? 0} /></Grid>
        <Grid item xs={6} sm={4} md={2}><KpiCard label="Bathroom" value={kpis?.bathroom ?? 0} /></Grid>
        <Grid item xs={6} sm={4} md={2}><KpiCard label="Agent Problems" value={kpis?.agent_problems ?? 0} /></Grid>
        <Grid item xs={6} sm={4} md={2}><KpiCard label="Campaign Mismatch" value={kpis?.campaign_mismatches ?? 0} /></Grid>
        <Grid item xs={6} sm={4} md={2}><KpiCard label="Emergency" value={kpis?.emergency_bypass ?? 0} /></Grid>
      </Grid>

      <EmergencyQuickActions />

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>Needs Attention</Typography>
          {(overview?.needs_attention || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No active attention items.</Typography>
          ) : (
            <List dense>
              {(overview?.needs_attention || []).map((item: any) => (
                <ListItemButton
                  key={item.key}
                  onClick={() => navigate(item.filter_path + (item.filter_query ? `?${new URLSearchParams(item.filter_query).toString()}` : ''))}
                >
                  <ListItemText
                    primary={item.label}
                    secondary={`Severity: ${item.severity}`}
                  />
                  <Chip size="small" color={item.severity === 'error' ? 'error' : item.severity === 'warning' ? 'warning' : 'info'} label={item.count} />
                </ListItemButton>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OverviewPage;
